import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from "@chakra-ui/react";
import { 
  FiRefreshCw, 
  FiEye, 
  FiClock,
  FiDollarSign,
  FiUsers,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiTrendingUp,
  FiActivity,
  FiCreditCard
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { 
  useGetPaymentTracking, 
  useGetBatchStatus, 
  useRetryFailedPayments 
} from "../../../services/payrollService";

const PaymentTracking = () => {
  const { user } = useUserContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // Comprehensive API data from single endpoint
  const { 
    data: paymentTrackingResponse, 
    isLoading: paymentTrackingLoading, 
    error: paymentTrackingError 
  } = useGetPaymentTracking();
  
  const retryFailedPaymentsMutation = useRetryFailedPayments();
  
  // Extract data from the comprehensive response
  const paymentTrackingData = paymentTrackingResponse?.payment_tracking || [];
  const summary = paymentTrackingResponse?.summary;
  const filterInfo = paymentTrackingResponse?.filter_info;
  const totalCount = paymentTrackingResponse?.total_count || 0;
  
  // Process batches by status
  const processingBatches = paymentTrackingData.filter(batch => 
    batch.payment_status?.toLowerCase() === 'processing'
  );
  const completedBatches = paymentTrackingData.filter(batch => {
    const status = batch.payment_status?.toLowerCase();
    return status === 'paid' || status === 'completed' || status === 'success';
  });
  const failedBatches = paymentTrackingData.filter(batch => 
    batch.payment_status?.toLowerCase() === 'failed'
  );
  const pendingBatches = paymentTrackingData.filter(batch => 
    batch.payment_status?.toLowerCase() === 'pending' || 
    batch.payment_status?.toLowerCase() === 'pending_approval'
  );
  const rejectedBatches = paymentTrackingData.filter(batch => 
    batch.payment_status?.toLowerCase() === 'rejected'
  );

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    onOpen();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'success': return 'green';
      case 'processing': return 'blue';
      case 'failed': return 'red';
      case 'pending':
      case 'pending_approval': return 'orange';
      case 'rejected': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID': return 'Paid';
      case 'COMPLETED': return 'Completed';
      case 'SUCCESS': return 'Success';
      case 'PROCESSING': return 'Processing';
      case 'FAILED': return 'Failed';
      case 'PENDING': return 'Pending';
      case 'PENDING_APPROVAL': return 'Pending Approval';
      case 'REJECTED': return 'Rejected';
      default: return status || 'Unknown';
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: "$",
      NGN: "₦",
      GHS: "₵",
      KES: "KSh",
      ZAR: "R"
    };
    return symbols[currency] || currency;
  };

  const handleRetryFailedPayments = (batchId) => {
    retryFailedPaymentsMutation.mutate(batchId);
  };

  const calculateProgress = (batch) => {
    if (!batch.employee_details || batch.employee_details.length === 0) return 0;
    const completed = batch.employee_details.filter(item => 
      item.payment_status?.toLowerCase() === 'paid' || 
      item.payment_status?.toLowerCase() === 'completed' ||
      item.payment_status?.toLowerCase() === 'success'
    ).length;
    return (completed / batch.employee_details.length) * 100;
  };

  const getFailedItemsCount = (batch) => {
    return batch.employee_details ? batch.employee_details.filter(item => 
      item.payment_status?.toLowerCase() === 'failed'
    ).length : 0;
  };

  const getCompletedItemsCount = (batch) => {
    return batch.employee_details ? batch.employee_details.filter(item => 
      item.payment_status?.toLowerCase() === 'paid' || 
      item.payment_status?.toLowerCase() === 'completed' ||
      item.payment_status?.toLowerCase() === 'success'
    ).length : 0;
  };

  // Show loading state
  if (paymentTrackingLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              Payment Tracking
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Loading payment data...
            </Text>
          </VStack>
        </Flex>
        <Box textAlign="center" py={8}>
          <Text color="gray.500">Loading payment tracking data...</Text>
        </Box>
      </VStack>
    );
  }

  // Show error state
  if (paymentTrackingError) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              Payment Tracking
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Error loading payment data
            </Text>
          </VStack>
        </Flex>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading payment data!</AlertTitle>
          <AlertDescription>
            {paymentTrackingError?.message || 'Failed to load payment tracking data'}
          </AlertDescription>
        </Alert>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.800">
            Payment Tracking
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Monitor payment status and retry failed payments
          </Text>
        </VStack>
        <Button
          leftIcon={<FiRefreshCw />}
          colorScheme="blue"
          variant="outline"
          size="md"
          borderRadius="lg"
          onClick={() => window.location.reload()}
        >
          Refresh Status
        </Button>
      </Flex>

      {/* Summary Statistics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6}>
        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Total Payments</StatLabel>
          <StatNumber color="blue.500">{summary?.total_payments || totalCount}</StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            All batches
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Processing</StatLabel>
          <StatNumber color="blue.500">{processingBatches.length}</StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            In progress
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Completed</StatLabel>
          <StatNumber color="green.500">{completedBatches.length}</StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            Successfully paid
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Failed</StatLabel>
          <StatNumber color="red.500">{failedBatches.length}</StatNumber>
          <StatHelpText>
            <StatArrow type="decrease" />
            Need retry
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Success Rate</StatLabel>
          <StatNumber color="green.500">{(summary?.success_rate || 0).toFixed(1)}%</StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            Payment success
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Payment Status Tabs */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>Pending Approval ({pendingBatches.length})</Tab>
            <Tab>Processing ({processingBatches.length})</Tab>
            <Tab>Completed ({completedBatches.length})</Tab>
            <Tab>Failed ({failedBatches.length})</Tab>
            <Tab>Rejected ({rejectedBatches.length})</Tab>
          </TabList>

          <TabPanels>
            {/* Processing Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                {processingBatches.length > 0 ? (
                  processingBatches.map((batch) => (
                    <Box key={batch.batch_id} p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
                      <VStack spacing={4} align="stretch">
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="600" color="gray.800">
                              {batch.batch_name}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {batch.batch_id} • {getCurrencySymbol(batch.payment_progress?.currency || 'NGN')}{(batch.payment_progress?.total_amount || 0).toLocaleString()}
                            </Text>
                          </VStack>
                          <Badge colorScheme={getStatusColor(batch.payment_status)}>
                            {getStatusText(batch.payment_status)}
                          </Badge>
                        </HStack>

                        <Box>
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" color="gray.600">
                              Progress: {getCompletedItemsCount(batch)}/{batch.employee_details?.length || 0} payments
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {calculateProgress(batch).toFixed(1)}%
                            </Text>
                          </HStack>
                          <Progress value={calculateProgress(batch)} colorScheme="blue" size="sm" />
                        </Box>

                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Payment Batch: {batch.flutterwave_batch_id || 'Not assigned'}
                          </Text>
                          <HStack spacing={2}>
                            <Button size="sm" variant="ghost" colorScheme="blue" onClick={() => handleViewBatch(batch)}>
                              View Details
                            </Button>
                            {getFailedItemsCount(batch) > 0 && (
                              <Button 
                                size="sm" 
                                colorScheme="orange" 
                                variant="outline"
                                onClick={() => handleRetryFailedPayments(batch.batch_id)}
                              >
                                Retry Failed ({getFailedItemsCount(batch)})
                              </Button>
                            )}
                          </HStack>
                        </HStack>
                      </VStack>
                    </Box>
                  ))
                ) : (
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>
                      No batches are currently being processed.
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </TabPanel>

            {/* Completed Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                {completedBatches.length > 0 ? (
                  completedBatches.map((batch) => (
                    <Box key={batch.batch_id} p={4} bg="green.50" borderRadius="lg" border="1px solid" borderColor="green.200">
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600" color="gray.800">
                            {batch.batch_name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {batch.batch_id} • {getCurrencySymbol(batch.payment_progress?.currency || 'NGN')}{(batch.payment_progress?.total_amount || 0).toLocaleString()}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Completed: {new Date(batch.completed_at).toLocaleString()}
                          </Text>
                        </VStack>
                        <HStack spacing={2}>
                          <Badge colorScheme={getStatusColor(batch.payment_status)}>
                            {getStatusText(batch.payment_status)}
                          </Badge>
                          <Button size="sm" variant="ghost" colorScheme="green" onClick={() => handleViewBatch(batch)}>
                            View Details
                          </Button>
                        </HStack>
                      </HStack>
                    </Box>
                  ))
                ) : (
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>
                      No completed batches found.
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </TabPanel>

            {/* Failed Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                {failedBatches.length > 0 ? (
                  failedBatches.map((batch) => (
                    <Box key={batch.batch_id} p={4} bg="red.50" borderRadius="lg" border="1px solid" borderColor="red.200">
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600" color="gray.800">
                            {batch.batch_name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {batch.batch_id} • {getCurrencySymbol(batch.payment_progress?.currency || 'NGN')}{(batch.payment_progress?.total_amount || 0).toLocaleString()}
                          </Text>
                        </VStack>
                        <HStack spacing={2}>
                          <Badge colorScheme={getStatusColor(batch.payment_status)}>
                            {getStatusText(batch.payment_status)}
                          </Badge>
                          <Button size="sm" colorScheme="red" variant="outline" onClick={() => handleRetryFailedPayments(batch.batch_id)}>
                            Retry Payments
                          </Button>
                          <Button size="sm" variant="ghost" colorScheme="red" onClick={() => handleViewBatch(batch)}>
                            View Details
                          </Button>
                        </HStack>
                      </HStack>
                    </Box>
                  ))
                ) : (
                  <Alert status="success" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>
                      No failed batches! All payments are processing successfully.
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </TabPanel>

            {/* Pending Approval Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                {pendingBatches.length > 0 ? (
                  pendingBatches.map((batch) => (
                    <Box key={batch.batch_id} p={4} bg="orange.50" borderRadius="lg" border="1px solid" borderColor="orange.200">
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600" color="gray.800">
                            {batch.batch_name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {batch.batch_id} • {getCurrencySymbol(batch.payment_progress?.currency || 'NGN')}{(batch.payment_progress?.total_amount || 0).toLocaleString()}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Created: {new Date(batch.created_at).toLocaleString()}
                          </Text>
                          {batch.approved_by_hr && (
                            <Text fontSize="sm" color="green.600">
                              ✅ HR Approved by {batch.approved_by_hr}
                            </Text>
                          )}
                        </VStack>
                        <HStack spacing={2}>
                          <Badge colorScheme={getStatusColor(batch.payment_status)}>
                            {getStatusText(batch.payment_status)}
                          </Badge>
                          <Button size="sm" variant="ghost" colorScheme="orange" onClick={() => handleViewBatch(batch)}>
                            View Details
                          </Button>
                        </HStack>
                      </HStack>
                    </Box>
                  ))
                ) : (
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>
                      No batches pending approval.
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </TabPanel>

            {/* Rejected Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                {rejectedBatches.length > 0 ? (
                  rejectedBatches.map((batch) => (
                    <Box key={batch.batch_id} p={4} bg="red.50" borderRadius="lg" border="1px solid" borderColor="red.200">
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600" color="gray.800">
                            {batch.batch_name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {batch.batch_id} • {getCurrencySymbol(batch.payment_progress?.currency || 'NGN')}{(batch.payment_progress?.total_amount || 0).toLocaleString()}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Created: {new Date(batch.created_at).toLocaleString()}
                          </Text>
                        </VStack>
                        <HStack spacing={2}>
                          <Badge colorScheme={getStatusColor(batch.payment_status)}>
                            {getStatusText(batch.payment_status)}
                          </Badge>
                          <Button size="sm" variant="ghost" colorScheme="red" onClick={() => handleViewBatch(batch)}>
                            View Details
                          </Button>
                        </HStack>
                      </HStack>
                    </Box>
                  ))
                ) : (
                  <Alert status="success" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>
                      No rejected batches found.
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Batch Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Payment Details: {selectedBatch?.batch_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedBatch && (
              <VStack spacing={6} align="stretch">
                {/* Batch Summary */}
                <Box bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Total Amount</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {getCurrencySymbol(selectedBatch.payment_progress?.currency || 'NGN')}{(selectedBatch.payment_progress?.total_amount || 0).toLocaleString()}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Employee Count</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {selectedBatch.employee_count}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Completed</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {getCompletedItemsCount(selectedBatch)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Failed</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {getFailedItemsCount(selectedBatch)}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                {/* Payment Items */}
                {selectedBatch.employee_details && selectedBatch.employee_details.length > 0 && (
                  <Box>
                    <Heading size="sm" color="gray.800" mb={4}>
                      Payment Items
                    </Heading>
                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Employee</Th>
                            <Th>Net Salary</Th>
                            <Th>Pay Type</Th>
                            <Th>Status</Th>
                            <Th>Refund Amount</Th>
                            <Th>Batch Transfer ID</Th>
                            <Th>Payment Reference</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {selectedBatch.employee_details.map((item) => (
                            <Tr key={item.employee_id}>
                              <Td>
                                <VStack align="start" spacing={1}>
                                <Text fontWeight="600" color="gray.800">
                                  {item.employee_name}
                                </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    ID: {item.employee_id}
                                  </Text>
                                </VStack>
                              </Td>
                              <Td>
                                <Text fontWeight="600" color="gray.800">
                                  {getCurrencySymbol(item.currency)}{item.net_salary.toLocaleString()}
                                </Text>
                              </Td>
                              <Td>
                                <Badge colorScheme="blue" variant="subtle">
                                  Net Salary
                                </Badge>
                              </Td>
                              <Td>
                                <VStack align="start" spacing={1}>
                                <Badge colorScheme={getStatusColor(item.payment_status)}>
                                  {getStatusText(item.payment_status)}
                                </Badge>
                                  {item.failure_reason && (
                                    <Text fontSize="xs" color="red.600">
                                      {item.failure_reason}
                                    </Text>
                                  )}
                                </VStack>
                              </Td>
                              <Td>
                                {item.payment_status?.toLowerCase() === 'failed' && item.refund_amount ? (
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="600" color="red.600">
                                      {getCurrencySymbol(item.currency)}{item.refund_amount.toLocaleString()}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      Will be refunded
                                    </Text>
                                  </VStack>
                                ) : (
                                  <Text fontSize="sm" color="gray.400">—</Text>
                                )}
                              </Td>
                              <Td>
                                <Text fontSize="sm" color="gray.600" fontFamily="mono">
                                  {selectedBatch.flutterwave_batch_id || 'Not assigned'}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" color="gray.600">
                                  {selectedBatch.payment_reference || 'N/A'}
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            {selectedBatch && getFailedItemsCount(selectedBatch) > 0 && (
              <Button colorScheme="orange" onClick={() => handleRetryFailedPayments(selectedBatch.batch_id)}>
                Retry Failed Payments
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default PaymentTracking;